import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog";
import { Button } from "@/project/karuna/components/ui/button";
import { Label } from "@/project/karuna/components/ui/label";
import { Checkbox } from "@/project/karuna/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/karuna/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs";
import { useToast } from "@/project/karuna/hooks/use-toast";
import {
  autoMapExcelColumns,
  exportImportLogSheet,
  exportSchoolsToExcel,
  exportSchoolTemplateExcel,
  parseExcelFile,
  SCHOOL_EXPORT_FIELDS,
  type SchemaFieldOption,
} from "@/project/karuna/utils/schoolExcelOperations";
import {
  processSchoolImportRows,
  type ImportBatchResult,
} from "@/project/karuna/utils/schoolImportRowEngine";
import { getImportAuditStorageInfo } from "@/project/karuna/utils/schoolImportAudit";
import { SchoolImportLogTable } from "@/project/karuna/components/admin/SchoolImportLogTable";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";

const NONE_COLUMN_VALUE = "__none__";

interface ExistingSchool {
  id: string;
  school_name: string;
  contact_number: string;
  kc_no: string;
}

interface SchoolExcelImportExportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "import" | "export";
  schoolRecords: Record<string, any>[];
  existingSchools: ExistingSchool[];
  onComplete: () => void;
}

export function SchoolExcelImportExport({
  open,
  onOpenChange,
  defaultTab = "export",
  schoolRecords,
  existingSchools,
  onComplete,
}: SchoolExcelImportExportProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<"import" | "export">(defaultTab);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [excelRows, setExcelRows] = useState<Record<string, unknown>[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(
    {}
  );
  const [selectedExportFields, setSelectedExportFields] = useState<
    Record<string, boolean>
  >({});
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    processed: number;
    total: number;
  } | null>(null);
  const [importBatchResult, setImportBatchResult] =
    useState<ImportBatchResult | null>(null);

  const schemaFields = SCHOOL_EXPORT_FIELDS;
  const schemaLoading = false;

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      setImportBatchResult(null);
      setImportProgress(null);
    }
  }, [open, defaultTab]);

  useEffect(() => {
    if (schemaFields.length === 0) {
      return;
    }

    setSelectedExportFields(prev => {
      if (Object.keys(prev).length > 0) {
        return prev;
      }

      return schemaFields.reduce<Record<string, boolean>>((acc, field) => {
        acc[field.value] = true;
        return acc;
      }, {});
    });
  }, [schemaFields]);

  const selectedFieldsForExport = useMemo(
    () =>
      schemaFields.filter(field => selectedExportFields[field.value] !== false),
    [schemaFields, selectedExportFields]
  );

  const resetImportState = () => {
    setExcelHeaders([]);
    setExcelRows([]);
    setColumnMapping({});
    setUploadedFileName("");
    setImportBatchResult(null);
    setImportProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = async (file: File | undefined) => {
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !["xlsx", "xls", "csv"].includes(extension)) {
      toast({
        title: "Invalid file",
        description: "Please upload an Excel file (.xlsx, .xls) or .csv",
        variant: "destructive",
      });
      return;
    }

    try {
      const { headers, rows } = await parseExcelFile(file);
      setExcelHeaders(headers);
      setExcelRows(rows);
      setUploadedFileName(file.name);
      setImportBatchResult(null);
      setImportProgress(null);
      setColumnMapping(autoMapExcelColumns(headers, schemaFields));

      toast({
        title: "File loaded",
        description: `Found ${rows.length} row(s) with ${headers.length} column(s).`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to read file",
        description: error.message ?? "Could not parse the Excel file.",
        variant: "destructive",
      });
      resetImportState();
    }
  };

  const handleImport = async () => {
    const activeMappings = Object.entries(columnMapping).filter(
      ([, excelColumn]) => excelColumn && excelColumn !== NONE_COLUMN_VALUE
    );

    if (activeMappings.length === 0) {
      toast({
        title: "Mapping required",
        description: "Map at least one Excel column to a school field.",
        variant: "destructive",
      });
      return;
    }

    if (excelRows.length === 0) {
      toast({
        title: "No data rows",
        description: "Upload an Excel file with at least one data row.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    setImportBatchResult(null);
    setImportProgress({ processed: 0, total: excelRows.length });

    try {
      const result = await processSchoolImportRows({
        excelRows,
        columnMapping,
        existingSchools,
        sourceFileName: uploadedFileName,
        onProgress: (processed, total) => {
          setImportProgress({ processed, total });
        },
      });

      setImportBatchResult(result);

      if (result.summary.success_count > 0) {
        onComplete();
      }

      const failedCount = result.summary.failed_count;
      toast({
        title:
          failedCount > 0 ? "Import completed with errors" : "Import complete",
        description:
          failedCount > 0
            ? `${result.summary.success_count} of ${result.summary.total_rows} rows imported. ${failedCount} row(s) failed — see the failed rows below for reasons.`
            : `${result.summary.success_count} of ${result.summary.total_rows} rows imported (${result.summary.created_count} created, ${result.summary.updated_count} updated).`,
        variant: failedCount > 0 ? "destructive" : undefined,
      });
    } catch (error: unknown) {
      toast({
        title: "Import interrupted",
        description:
          error instanceof Error
            ? error.message
            : "Could not process the import batch.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  const handleDownloadImportLog = () => {
    if (!importBatchResult) {
      return;
    }

    const { success_logs, failed_logs, summary } = importBatchResult;
    const stamp = new Date().toISOString().split("T")[0];
    const shortBatch = summary.import_batch_id.slice(0, 8);

    // Combined log: every row with its Status (Success/Failed) and reason, by row number.
    const combinedLogs = [...success_logs, ...failed_logs].sort(
      (a, b) => a.row_number - b.row_number
    );

    try {
      if (combinedLogs.length > 0) {
        exportImportLogSheet(
          combinedLogs,
          `import_log_${stamp}_${shortBatch}`,
          "Import Log"
        );
      }
    } catch (error: unknown) {
      toast({
        title: "Download failed",
        description:
          error instanceof Error
            ? error.message
            : "Could not export result file.",
        variant: "destructive",
      });
    }
  };

  const handleExport = () => {
    if (selectedFieldsForExport.length === 0) {
      toast({
        title: "Select fields",
        description: "Choose at least one field to export.",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);
    try {
      const dateStamp = new Date().toISOString().split("T")[0];
      exportSchoolsToExcel(
        schoolRecords,
        selectedFieldsForExport,
        `schools_export_${dateStamp}`
      );
      toast({
        title: "Export complete",
        description: `Exported ${schoolRecords.length} school(s) to Excel.`,
      });
    } catch (error: any) {
      toast({
        title: "Export failed",
        description: error.message ?? "Could not export schools.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (schemaFields.length === 0) {
      return;
    }

    exportSchoolTemplateExcel(schemaFields, "schools_import_template");
    toast({
      title: "Template downloaded",
      description: "Use this template column headers for importing schools.",
    });
  };

  const updateMapping = (dbField: string, excelColumn: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [dbField]: excelColumn,
    }));
  };

  const renderMappingSelect = (field: SchemaFieldOption) => (
    <div
      key={field.value}
      className="grid grid-cols-1 items-center gap-2 rounded-lg border p-3 md:grid-cols-2"
    >
      <div>
        <p className="text-sm font-medium">{field.label}</p>
        <p className="text-xs text-muted-foreground">{field.value}</p>
      </div>
      <Select
        value={columnMapping[field.value] ?? NONE_COLUMN_VALUE}
        onValueChange={value => updateMapping(field.value, value)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Excel column" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_COLUMN_VALUE}>— Not mapped —</SelectItem>
          {excelHeaders.map(header => (
            <SelectItem key={header} value={header}>
              {header}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        onOpenChange(value);
        if (!value) {
          resetImportState();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <FileSpreadsheet className="h-5 w-5" />
            Excel Import / Export
          </DialogTitle>
          <DialogDescription>
            Smart import auto-cleans the fields in each row. Every row is
            imported as a new school — duplicate names or KC Nos are kept as
            separate records.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={value => setActiveTab(value as "import" | "export")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="export">Export</TabsTrigger>
            <TabsTrigger value="import">Import</TabsTrigger>
          </TabsList>

          <TabsContent value="export" className="space-y-4 pt-4">
            {schemaLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <p className="font-medium">Export scope</p>
                  <p className="mt-1 text-muted-foreground">
                    {schoolRecords.length} approved school(s) matching your
                    current search filter will be exported.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Fields to export</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedExportFields(
                            schemaFields.reduce<Record<string, boolean>>(
                              (acc, field) => {
                                acc[field.value] = true;
                                return acc;
                              },
                              {}
                            )
                          )
                        }
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedExportFields(
                            schemaFields.reduce<Record<string, boolean>>(
                              (acc, field) => {
                                acc[field.value] = false;
                                return acc;
                              },
                              {}
                            )
                          )
                        }
                      >
                        Clear all
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border p-3">
                    {schemaFields.map(field => (
                      <label
                        key={field.value}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={selectedExportFields[field.value] !== false}
                          onCheckedChange={checked =>
                            setSelectedExportFields(prev => ({
                              ...prev,
                              [field.value]: checked === true,
                            }))
                          }
                        />
                        <span className="text-sm">
                          {field.label}
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({field.value})
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="import" className="space-y-4 pt-4">
            {schemaLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={event =>
                      handleFileSelect(event.target.files?.[0])
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Excel
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-2"
                    onClick={handleDownloadTemplate}
                  >
                    <Download className="h-4 w-4" />
                    Download template
                  </Button>
                  {uploadedFileName ? (
                    <span className="text-sm text-muted-foreground">
                      {uploadedFileName} · {excelRows.length} row(s)
                    </span>
                  ) : null}
                </div>

                {excelHeaders.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base">Column mapping</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setColumnMapping(
                            autoMapExcelColumns(excelHeaders, schemaFields)
                          )
                        }
                      >
                        Auto-map columns
                      </Button>
                    </div>
                    <div className="max-h-72 space-y-2 overflow-y-auto">
                      {schemaFields.map(renderMappingSelect)}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    Upload an Excel file to map columns and import schools.
                  </div>
                )}

                {importProgress ? (
                  <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <p>
                        Processing rows {importProgress.processed} of{" "}
                        {importProgress.total}...
                      </p>
                    </div>
                  </div>
                ) : null}

                {importBatchResult ? (
                  <div className="space-y-4 rounded-lg border p-4 text-sm">
                    <div>
                      <p className="font-medium">Import summary</p>
                      <p className="mt-1 text-muted-foreground">
                        Batch ID: {importBatchResult.summary.import_batch_id}
                      </p>
                      <p className="mt-2 font-medium text-emerald-700">
                        {importBatchResult.summary.success_count} of{" "}
                        {importBatchResult.summary.total_rows} rows imported
                        successfully
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        {importBatchResult.summary.created_count} created ·{" "}
                        {importBatchResult.summary.updated_count} updated
                      </p>
                      {importBatchResult.summary.failed_count > 0 ? (
                        <p className="mt-1 font-medium text-red-700">
                          {importBatchResult.summary.failed_count} of{" "}
                          {importBatchResult.summary.total_rows} rows failed to
                          import — see reasons below.
                        </p>
                      ) : null}
                      {importBatchResult.audit_records.some(
                        r => r.persisted_to === "local"
                      ) ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Audit logs saved locally (
                          {getImportAuditStorageInfo().schema_name} schema not
                          found on server).
                        </p>
                      ) : null}
                    </div>

                    {importBatchResult.success_logs.length > 0 ||
                    importBatchResult.failed_logs.length > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={handleDownloadImportLog}
                      >
                        <Download className="h-4 w-4" />
                        Download import log
                      </Button>
                    ) : null}

                    {importBatchResult.failed_logs.length > 0 ? (
                      <div className="space-y-2">
                        <p className="font-medium text-red-700">
                          Failed rows ({importBatchResult.failed_logs.length})
                        </p>
                        <SchoolImportLogTable
                          logs={importBatchResult.failed_logs}
                        />
                      </div>
                    ) : null}

                    {importBatchResult.success_logs.length > 0 ? (
                      <div className="space-y-2">
                        <p className="font-medium text-emerald-700">
                          Imported rows ({importBatchResult.success_logs.length}
                          )
                        </p>
                        <SchoolImportLogTable
                          logs={importBatchResult.success_logs}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {activeTab === "export" ? (
            <Button
              type="button"
              className="gap-2"
              disabled={isExporting || schemaLoading}
              onClick={handleExport}
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export Excel
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              className="gap-2"
              disabled={isImporting || schemaLoading || excelRows.length === 0}
              onClick={handleImport}
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import schools
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
